import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import lessonData from '../data/lessons-index';
import { useGame } from '../context/GameContext';
import './LearningModule.css';

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const LearningModule = () => {
  const { topic } = useParams();
  const { awardXP, resetStreak, currentStreak } = useGame();
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [streak, setStreak] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [randomizedLessons, setRandomizedLessons] = useState([]);
  const [difficulty, setDifficulty] = useState('all');
  const [lessonStartTime, setLessonStartTime] = useState(Date.now());
  const inputRef = useRef(null);

  // Helper to determine the base set length for lessons that are repeated
  const getBaseLength = (lessons) => {
    if (lessons.length === 0) return 0;
    const firstTitle = lessons[0].title;
    for (let i = 1; i < lessons.length; i++) {
      if (lessons[i].title === firstTitle) {
        return i;
      }
    }
    return lessons.length;
  };

  // Filter lessons by difficulty selection
  const filterByDifficulty = (lessons, level) => {
    if (level === 'all') return lessons;

    // Check if lessons have the new difficulty property
    const hasNewFormat = lessons.length > 0 && lessons[0].difficulty;

    if (hasNewFormat) {
      // New format: filter by explicit difficulty level
      if (level === 'beginner') {
        return lessons.filter(l => l.difficulty === 'beginner');
      }
      if (level === 'intermediate') {
        return lessons.filter(l => l.difficulty === 'intermediate');
      }
      if (level === 'advanced') {
        return lessons.filter(l => l.difficulty === 'advanced');
      }
      if (level === 'expert') {
        return lessons.filter(l => l.difficulty === 'expert');
      }
      // Fallback for old format options
      if (level === 'basic') {
        return lessons.filter(l => l.difficulty === 'beginner' || l.difficulty === 'intermediate');
      }
      return lessons;
    }

    // Legacy format: use index-based filtering
    const baseLen = getBaseLength(lessons);
    if (baseLen === 0) return lessons;

    if (level === 'basic' || level === 'beginner') {
      return lessons.filter((_, idx) => idx % baseLen < Math.ceil(baseLen / 2));
    }
    if (level === 'advanced') {
      return lessons.filter((_, idx) => idx % baseLen >= Math.ceil(baseLen / 2));
    }
    if (level === 'common') {
      return lessons.filter((_, idx) => idx % baseLen < 5);
    }
    return lessons;
  };

  // Initialize or update lessons when topic or difficulty changes
  useEffect(() => {
    const topicLessons = lessonData[topic] || [];
    const filtered = filterByDifficulty(topicLessons, difficulty);
    setRandomizedLessons(shuffleArray(filtered));

    // Reset state when topic or difficulty changes
    setCurrentLessonIndex(0);
    setUserInput('');
    setFeedback(null);
    setShowHint(false);
    setShowAnswer(false);
    setStreak(0);
    setCompleted(false);
    setLessonStartTime(Date.now());
  }, [topic, difficulty]);

  // Reset lesson start time when moving to a new lesson
  useEffect(() => {
    setLessonStartTime(Date.now());
  }, [currentLessonIndex]);
  
  // Get current lesson from randomized array
  const currentLesson = randomizedLessons[currentLessonIndex];
  
  const handleInputChange = (e) => {
    setUserInput(e.target.value);
  };
  
  const checkAnswer = () => {
    if (!currentLesson) return;

    const isCorrect = checkIfCorrect(userInput, currentLesson.answer);

    if (isCorrect) {
      // Calculate time taken
      const timeInSeconds = (Date.now() - lessonStartTime) / 1000;

      // Determine difficulty (use lesson's difficulty or default to 'beginner')
      const lessonDifficulty = currentLesson.difficulty || 'beginner';

      // Award XP using the game context
      const xpGained = awardXP(lessonDifficulty, {
        usedHint: showHint,
        usedAnswer: showAnswer,
        timeInSeconds,
        lessonId: currentLesson.id || `${topic}-${currentLessonIndex}`,
        topic
      });

      setFeedback({
        correct: true,
        message: `Correct! +${xpGained} XP`
      });
      setStreak(streak + 1);

      // Move to next lesson after a delay
      setTimeout(() => {
        if (currentLessonIndex < randomizedLessons.length - 1) {
          setCurrentLessonIndex(currentLessonIndex + 1);
          setUserInput('');
          setFeedback(null);
          setShowHint(false);
          setShowAnswer(false);
        } else {
          // All lessons completed
          setCompleted(true);
        }
      }, 1500);
    } else {
      setFeedback({
        correct: false,
        message: 'That\'s not quite right. Try again!'
      });
      setStreak(0);
      resetStreak(); // Also reset the global streak
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    checkAnswer();
  };
  
  const showHintHandler = () => {
    setShowHint(true);
  };
  
  const showAnswerHandler = () => {
    setShowAnswer(true);
  };

  // Helper function to check if answer is correct
  const checkIfCorrect = (input, answer) => {
    // Clean up both input and answer, removing extra spaces
    const cleanInput = input.trim();
    
    // For multiple acceptable answers
    if (Array.isArray(answer)) {
      return answer.some(ans => isCommandMatch(cleanInput, ans.trim()));
    }
    
    // For single answer
    return isCommandMatch(cleanInput, answer.trim());
  };
  
  // Helper function to intelligently compare commands
  const isCommandMatch = (userInput, expectedAnswer) => {
    // If exactMatch is required, do a precise comparison
    if (currentLesson && currentLesson.exactMatch) {
      return userInput === expectedAnswer;
    }
    
    // Case insensitive matching for regular comparisons
    if (userInput.toLowerCase() === expectedAnswer.toLowerCase()) {
      return true;
    }
    
    // Smart command matching for bash commands
    try {
      // Split both into command and args
      const userParts = parseCommand(userInput);
      const expectedParts = parseCommand(expectedAnswer);
      
      // Command base must match (e.g., 'grep' === 'grep')
      if (userParts.command.toLowerCase() !== expectedParts.command.toLowerCase()) {
        return false;
      }
      
      // Check if all required positional arguments are present in user input
      // Positional args are non-option arguments like file paths, search patterns, etc.
      for (const arg of expectedParts.positionalArgs) {
        if (!userParts.positionalArgs.some(userArg => 
          userArg.toLowerCase() === arg.toLowerCase()
        )) {
          return false;
        }
      }
      
      // For common commands, check if equivalent flags are used
      // This allows for variations like -r vs -R, or allowing additional flags
      if (isCommonCommand(userParts.command)) {
        if (!hasEquivalentOptions(userParts.options, expectedParts.options, userParts.command)) {
          return false;
        }
      } else {
        // For other commands, check that the user included at least the expected options
        // This allows adding additional options but requires all expected ones
        for (const option of expectedParts.options) {
          // Check if user has this exact option or an equivalent
          const hasOption = userParts.hasOption(option) ||
            (option.startsWith('--') && userParts.options.some(userOption => 
              userOption.startsWith('-') && userOption.substring(1) === option.substring(2)
            ));
          
          if (!hasOption) {
            return false;
          }
        }
      }
      
      // At this point, the command matches the expected command,
      // contains all required positional arguments, and all or equivalent options
      return true;
    } catch (error) {
      // If parsing fails, fall back to exact match
      return userInput.toLowerCase() === expectedAnswer.toLowerCase();
    }
  };
  
  // Helper function to parse a command into its components
  const parseCommand = (commandStr) => {
    const parts = commandStr.split(/\s+/);
    const command = parts[0];
    const allArgs = parts.slice(1);
    
    // Separate options (starting with - or --) from positional arguments
    const options = [];
    const expandedOptions = []; // For individual option characters from combined options
    
    allArgs.forEach(arg => {
      if (arg.startsWith('-')) {
        options.push(arg);
        
        // Handle combined short options like -la (equivalent to -l -a)
        if (arg.startsWith('-') && !arg.startsWith('--') && arg.length > 2) {
          // For each character after the dash, add it as an option
          for (let i = 1; i < arg.length; i++) {
            expandedOptions.push(`-${arg[i]}`);
          }
        }
      }
    });
    
    const positionalArgs = allArgs.filter(arg => !arg.startsWith('-'));
    
    return { 
      command, 
      allArgs, 
      options, 
      expandedOptions,
      positionalArgs,
      // Helper function to check if an option exists, considering both full and expanded
      hasOption: (opt) => options.includes(opt) || expandedOptions.includes(opt)
    };
  };
  
  // Check if command is a common Unix/Linux command with well-known option equivalents
  const isCommonCommand = (command) => {
    const commonCommands = ['grep', 'ls', 'find', 'ps', 'cp', 'mv', 'rm', 'chmod', 'chown', 'tar', 'sed', 'awk'];
    return commonCommands.includes(command.toLowerCase());
  };
  
  // Check if the user's options are equivalent to the expected options
  const hasEquivalentOptions = (userOptions, expectedOptions, command) => {
    // Empty expected options means no options are required
    if (expectedOptions.length === 0) {
      return true;
    }
    
    // Expand combined options like -la into individual options -l -a
    const expandUserOptions = (options) => {
      const expanded = [...options];
      options.forEach(opt => {
        if (opt.startsWith('-') && !opt.startsWith('--') && opt.length > 2) {
          for (let i = 1; i < opt.length; i++) {
            expanded.push(`-${opt[i]}`);
          }
        }
      });
      return expanded;
    };
    
    const expandedUserOptions = expandUserOptions(userOptions);
    
    // Handle specific commands with known option equivalents
    switch(command.toLowerCase()) {
      case 'grep':
        // Check if user has at least equivalent grep options 
        // (e.g., -r and -R are equivalent, -i can be added)
        return expectedOptions.every(expected => {
          // Handle specific grep option equivalents
          if (expected === '-r' || expected === '-R') {
            return expandedUserOptions.some(opt => opt === '-r' || opt === '-R' || opt === '--recursive');
          }
          if (expected === '-i') {
            return expandedUserOptions.some(opt => opt === '-i' || opt === '--ignore-case');
          }
          if (expected === '-v') {
            return expandedUserOptions.some(opt => opt === '-v' || opt === '--invert-match');
          }
          // For other options, require exact match
          return expandedUserOptions.includes(expected);
        });
      
      case 'ls':
        return expectedOptions.every(expected => {
          if (expected === '-l') {
            return expandedUserOptions.some(opt => opt === '-l' || opt.includes('l'));
          }
          if (expected === '-a') {
            return expandedUserOptions.some(opt => opt === '-a' || opt.includes('a') || opt === '--all');
          }
          return expandedUserOptions.includes(expected);
        });
        
      case 'sed':
        return expectedOptions.every(expected => {
          // Common sed options
          if (expected === '-e') {
            return expandedUserOptions.some(opt => opt === '-e' || opt === '--expression');
          }
          if (expected === '-i') {
            return expandedUserOptions.some(opt => opt === '-i' || opt === '--in-place');
          }
          if (expected === '-n') {
            return expandedUserOptions.some(opt => opt === '-n' || opt === '--quiet' || opt === '--silent');
          }
          return expandedUserOptions.includes(expected);
        });
        
      case 'awk':
        return expectedOptions.every(expected => {
          // Common awk options
          if (expected === '-F') {
            return expandedUserOptions.some(opt => opt === '-F' || opt.startsWith('-F') || opt === '--field-separator');
          }
          if (expected === '-v') {
            return expandedUserOptions.some(opt => opt === '-v' || opt.startsWith('-v'));
          }
          return expandedUserOptions.includes(expected);
        });
        
      default:
        // For other common commands, just ensure all expected options are present
        // This allows user to add additional options
        return expectedOptions.every(expected => expandedUserOptions.includes(expected));
    }
  };
  
  // Navigation handlers
  const goToNextLesson = () => {
    if (currentLessonIndex < randomizedLessons.length - 1) {
      setCurrentLessonIndex(currentLessonIndex + 1);
      setUserInput('');
      setFeedback(null);
      setShowHint(false);
      setShowAnswer(false);
    }
  };

  const goToPreviousLesson = () => {
    if (currentLessonIndex > 0) {
      setCurrentLessonIndex(currentLessonIndex - 1);
      setUserInput('');
      setFeedback(null);
      setShowHint(false);
      setShowAnswer(false);
    }
  };

  const goToSpecificLesson = (index) => {
    if (index >= 0 && index < randomizedLessons.length) {
      setCurrentLessonIndex(index);
      setUserInput('');
      setFeedback(null);
      setShowHint(false);
      setShowAnswer(false);
    }
  };

  // If no lessons are available
  if (randomizedLessons.length === 0) {
    return (
      <div className="learning-module empty-state">
        <Link to="/topics" className="back-link">← Back to Topics</Link>
        <div className="empty-container">
          <h2>Coming Soon!</h2>
          <p>Lessons for {topic} are currently being developed.</p>
          <Link to="/topics" className="btn">Choose Another Topic</Link>
        </div>
      </div>
    );
  }
  
  // If all lessons are completed
  if (completed) {
    return (
      <div className="learning-module completion">
        <div className="completion-container">
          <div className="completion-icon">🎉</div>
          <h2>Congratulations!</h2>
          <p>You've completed all the lessons for {currentLesson.topic}.</p>
          <Link to="/topics" className="btn">Choose Another Topic</Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="learning-module">
      <div className="header-container">
        <Link to="/topics" className="back-link">← Back to Topics</Link>
        <div className="lesson-controls">
          <div className="lesson-navigation">
            <button
              onClick={goToPreviousLesson}
              disabled={currentLessonIndex === 0}
              className="nav-button"
            >
              ← Previous
            </button>
            <span className="lesson-progress">
              Lesson {currentLessonIndex + 1} of {randomizedLessons.length}
            </span>
            <button
              onClick={goToNextLesson}
              disabled={currentLessonIndex === randomizedLessons.length - 1}
              className="nav-button"
            >
              Next →
            </button>
          </div>

          <div className="lesson-filters">
            <div className="filter-group">
              <label htmlFor="lesson-select">Jump to:</label>
              <select
                id="lesson-select"
                value={currentLessonIndex}
                onChange={(e) => goToSpecificLesson(parseInt(e.target.value))}
                className="lesson-select"
              >
                {randomizedLessons.map((lesson, index) => (
                  <option key={index} value={index}>
                    {index + 1}: {lesson.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="difficulty-select">Difficulty:</label>
              <select
                id="difficulty-select"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className="lesson-select difficulty-select"
              >
                <option value="all">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="module-header">
        <h1>{topic.charAt(0).toUpperCase() + topic.slice(1)} Commands</h1>
        <p className="learning-subtitle">Master {topic} commands through interactive exercises</p>
        <div className="randomized-badge">🎲 Randomized Lessons</div>
        
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentLessonIndex / randomizedLessons.length) * 100}%` }}
            ></div>
          </div>
          <div className="lesson-count">
            Lesson {currentLessonIndex + 1} of {randomizedLessons.length}
          </div>
          <div className="streak-counter">
            {streak > 0 && <span>🔥 {streak}</span>}
          </div>
        </div>
      </div>
      
      <div className="lesson-container">
        <div className="lesson-content">
          <div className="lesson-title-row">
            <h2>{currentLesson.title}</h2>
            {currentLesson.difficulty && (
              <span className={`difficulty-badge difficulty-${currentLesson.difficulty}`}>
                {currentLesson.difficulty}
              </span>
            )}
            {currentLesson.xpReward && (
              <span className="xp-reward-badge">+{currentLesson.xpReward} XP</span>
            )}
          </div>
          <div className="lesson-description">
            {currentLesson.description}
          </div>
          
          <div className="terminal-container">
            <div className="terminal-header">
              <div className="terminal-buttons">
                <span className="terminal-btn red"></span>
                <span className="terminal-btn yellow"></span>
                <span className="terminal-btn green"></span>
              </div>
              <div className="terminal-title">Terminal</div>
            </div>
            <div className="terminal">
              <div className="terminal-prompt">
                <span className="prompt-user">user@machine</span>
                <span className="prompt-separator">:</span>
                <span className="prompt-directory">~</span>
                <span className="prompt-symbol">$</span>
              </div>
              
              <form onSubmit={handleSubmit} className="terminal-input-form">
                <input
                  type="text"
                  value={userInput}
                  onChange={handleInputChange}
                  className="terminal-input"
                  placeholder="Type your command here..."
                  autoFocus
                />
              </form>
            </div>
          </div>
          
          {feedback && (
            <div className={`feedback ${feedback.correct ? 'correct' : 'incorrect'}`}>
              {feedback.message}
            </div>
          )}
          
          <div className="lesson-actions">
            <button 
              onClick={showHintHandler} 
              className="btn btn-secondary hint-button" 
              disabled={showHint || !currentLesson}
            >
              Hint
            </button>
            <button 
              onClick={showAnswerHandler} 
              className="btn btn-warning answer-button" 
              disabled={showAnswer || !currentLesson}
            >
              Show Answer
            </button>
            <button
              type="button"
              onClick={checkAnswer}
              className="btn check-button"
              disabled={!userInput || !currentLesson}
            >
              Check Answer
            </button>
          </div>
          
          {showHint && (
            <div className="hint">
              <h3>Hint:</h3>
              <p>{currentLesson.hint}</p>
              {currentLesson.example && (
                <div className="example">
                  <h4>Example:</h4>
                  <code>{currentLesson.example}</code>
                </div>
              )}
            </div>
          )}
          
          {showAnswer && (
            <div className="answer">
              <h3>Answer:</h3>
              <pre><code>
                {/* Handle array answers */}
                {Array.isArray(currentLesson.answer) 
                  ? currentLesson.answer.join('\n') 
                  : currentLesson.answer}
              </code></pre>
            </div>
          )}
        </div>
        
        <div className="lesson-sidebar">
          <div className="info-box">
            <h3>Task</h3>
            <p>{currentLesson.task}</p>
          </div>

          {currentLesson.commandRef && (
            <div className="info-box command-ref-box">
              <h3>
                <span className="command-ref-icon">📖</span>
                Command: <code>{currentLesson.commandRef.command}</code>
              </h3>
              <p className="command-description">{currentLesson.commandRef.description}</p>

              {currentLesson.commandRef.syntax && (
                <div className="command-syntax">
                  <span className="syntax-label">Syntax:</span>
                  <code>{currentLesson.commandRef.syntax}</code>
                </div>
              )}

              {currentLesson.commandRef.options && currentLesson.commandRef.options.length > 0 && (
                <div className="command-options">
                  <h4>Options</h4>
                  <ul>
                    {currentLesson.commandRef.options.map((opt, index) => (
                      <li key={index}>
                        <code className="option-flag">{opt.flag}</code>
                        <span className="option-desc">{opt.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {currentLesson.commandRef.examples && currentLesson.commandRef.examples.length > 0 && (
                <div className="command-examples">
                  <h4>Examples</h4>
                  {currentLesson.commandRef.examples.map((ex, index) => (
                    <div key={index} className="example-item">
                      <code className="example-code">{ex.code}</code>
                      <span className="example-desc">{ex.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {currentLesson.tips && (
            <div className="info-box tips-box">
              <h3>
                <span className="tips-icon">💡</span>
                Tips
              </h3>
              <ul>
                {currentLesson.tips.map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearningModule;
